<script lang="ts">
  import { onMount } from "svelte";
  import { step } from "../../stores/WalkinBookingStore";

  // Add default export for component
  export {};

  // State variables
  let videoElement: HTMLVideoElement;
  let videoEnded = false;
  let isLoading = true;
  let isPlaying = false;
  let progress = 0;
  let loadingProgress = 0;
  let showPlayButton = true;

  // Function to execute when video ends
  function handleVideoEnd() {
    if (videoEnded) return; // Prevent multiple executions

    videoEnded = true;

    // Update the step store
    step.update((s) => s + 1);

    // Dispatch a custom event that other components can listen for
    const videoEndedEvent = new CustomEvent("videoEnded", {
      detail: { completed: true },
    });
    window.dispatchEvent(videoEndedEvent);

    // Show completion message
    const messageEl = document.getElementById("completion-message");
    if (messageEl) messageEl.style.display = "block";
  }

  // Update progress for display
  function updateProgress() {
    if (!videoElement) return;
    progress =
      Math.round((videoElement.currentTime / videoElement.duration) * 100) || 0;
  }

  // Play video function
  function playVideo() {
    if (!videoElement) return;
    videoElement
      .play()
      .then(() => {
        showPlayButton = false;
        isPlaying = true;
      })
      .catch((error) => {
        console.error("Error playing video:", error);
        // Keep play button visible if autoplay fails
        showPlayButton = true;
      });
  }

  // Handle video events
  function handleVideoEvents() {
    if (!videoElement) return;

    // Set up event listeners
    videoElement.addEventListener("loadedmetadata", () => {
      // Don't set isLoading=false yet, wait for more progress
    });

    videoElement.addEventListener("canplay", () => {
      isLoading = false;
    });

    videoElement.addEventListener("playing", () => {
      isPlaying = true;
      isLoading = false;
    });

    // Add progress event for loading progress
    videoElement.addEventListener("progress", () => {
      if (videoElement.buffered.length > 0) {
        const bufferedEnd = videoElement.buffered.end(
          videoElement.buffered.length - 1
        );
        loadingProgress =
          Math.round((bufferedEnd / videoElement.duration) * 100) || 0;

        // If we've loaded enough of the video, consider it ready to play
        if (loadingProgress > 15 && isLoading) {
          isLoading = false;
        }
      }
    });

    videoElement.addEventListener("pause", () => {
      isPlaying = false;
    });

    videoElement.addEventListener("ended", () => {
      step.update((s) => s + 1);
      isPlaying = false;
      handleVideoEnd();
    });

    videoElement.addEventListener("timeupdate", updateProgress);

    // Set up a backup check for video completion
    // This helps in case the 'ended' event doesn't fire for some reason
    const progressInterval = setInterval(() => {
      if (!videoElement) return;

      updateProgress();

      // If video is near the end (98% complete) and not already marked as ended
      if (
        videoElement.duration > 0 &&
        videoElement.currentTime > 0 &&
        videoElement.currentTime / videoElement.duration >= 0.98 &&
        !videoEnded
      ) {
        handleVideoEnd();
      }
    }, 1000);

    // Return cleanup function
    return () => {
      videoElement.removeEventListener("loadedmetadata", () => {});
      videoElement.removeEventListener("canplay", () => {});
      videoElement.removeEventListener("playing", () => {});
      videoElement.removeEventListener("pause", () => {});
      videoElement.removeEventListener("ended", () => {});
      videoElement.removeEventListener("timeupdate", updateProgress);
      clearInterval(progressInterval);
    };
  }

  // Manual trigger for testing
  function manuallyTriggerVideoEnd() {
    handleVideoEnd();
  }

  onMount(() => {
    videoEnded = false;

    // Set up video event listeners when component mounts
    const cleanup = handleVideoEvents();

    // Return cleanup function
    return () => {
      if (cleanup) cleanup();
      videoEnded = false;
    };
  });
</script>

<div class="w-full max-w-4xl mx-auto px-4 py-8">
  <div>
    <h2 class="text-2xl font-semibold uppercase text-center">
      Mandatory Video
    </h2>
    <p class="text-base text-center">Confirmation Message Shown After.</p>
    <p class="text-base font-semibold text-center font-semibold">
      YOU WILL NEED TO SHOW THE COACH.
    </p>
  </div>
  <div
    class="aspect-w-16 aspect-h-9 relative min-h-[600px] bg-black rounded-lg overflow-hidden"
  >
    <!-- Video element with direct S3 URL -->
    <video
      bind:this={videoElement}
      class="w-full h-full object-contain"
      preload="auto"
      playsinline
    >
      <source
        src="https://badaxe.s3.us-east-1.amazonaws.com/website/videos/badaxe-safety-video-720p-low.mp4"
        type="video/mp4"
      />
      Your browser does not support the video tag.
    </video>

    <!-- Progress bar overlay -->
    <div class="absolute bottom-0 left-0 w-full h-2 bg-gray-700">
      <div
        class="h-full bg-red-600 transition-all duration-300 ease-linear"
        style="width: {progress}%"
      ></div>
    </div>

    <!-- Loading overlay -->
    {#if isLoading}
      <div
        class="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center"
      >
        <div class="w-64 h-2 bg-gray-700 rounded-full overflow-hidden mb-4">
          <div
            class="h-full bg-blue-500 transition-all duration-300 ease-linear"
            style="width: {loadingProgress}%"
          ></div>
        </div>
        <p class="text-white">Loading: {loadingProgress}%</p>
      </div>
    {/if}

    <!-- Play button overlay -->
    {#if showPlayButton && !isPlaying}
      <div
        class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        role="region"
        aria-label="Video player controls"
      >
        <button
          class="bg-red-600 hover:bg-red-700 text-white rounded-full p-4 flex items-center justify-center transition-all focus:outline-none focus:ring-4 focus:ring-red-400"
          on:click={playVideo}
          on:keydown={(e) => e.key === "Enter" && playVideo()}
          aria-label="Play video with sound"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-16 w-16"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <span class="absolute bottom-10 text-white text-xl font-bold"
          >Click to Play with Sound</span
        >
      </div>
    {/if}
  </div>

  <!-- Video progress info -->
  <div class="mt-4 text-center">
    <span class="text-sm text-gray-600">Progress: {progress}%</span>
  </div>
</div>

<style>
  /* Add any component-specific styles here */
</style>
